import { compareWeights, isPackageProduct, normaliseWeight } from "./articleCode";

/**
 * Stuks per verpakking: how many of the units the webshop sells lie together in one package in
 * the freezer - per article, so per product and weight.
 *
 * Runderburgers are sold per burger ("per 4 te bestellen") but packed per four: an order for 8 is
 * two packages, and one scan of the vak counts a whole package of four. And it differs per weight:
 * kogelbiefstuk of 125 and 150 gram lies in pairs, from 200 gram up it is one steak per package.
 * A product the webshop already sells per package - "Kip drumsticks (4 stuks)", one unit is one
 * bag - is 1. So is everything that is not filled in, which is why 1 is stored as nothing: "per
 * stuk" has one spelling, and two linked products that both mean it never differ.
 *
 * Stored on the product in two fields, one per kind of article (functions/articleCode):
 *  - `piecesPerPack`, a number: the article without weight (a product without weight variants);
 *  - `piecesPerPackByWeight`, a map from weight to number: the articles per weight
 *    (`{ "125": 2, "150": 2 }`).
 * Never one for the other: a number filled in for "the product" must not quietly count the 1000
 * gram steak as a pair. A weight without a number is per piece.
 *
 * The number is about the unit of the order line, never about weight: the pick weight stays the
 * weight of one unit as sold (one burger), and box prediction keeps multiplying it by the
 * quantity ordered.
 *
 * Pure, and the one place that decides what a stored value means: the product screen, the sync
 * between the two shops and the wave document all read it through here.
 */

/** A typo guard: nothing in either catalogue comes near this many units in one package. */
export const MAX_PIECES_PER_PACK = 250;

const asNumber = (value) => {
  if (typeof value === "number") return value;
  if (typeof value !== "string") return NaN;
  const text = value.trim();
  return /^\d+$/.test(text) ? Number(text) : NaN;
};

/**
 * One stored value as a whole number of 2 or more, or null for "one unit per package": not set,
 * 1, or anything that is not a whole number in range. Firestore may hold a number or a numeric
 * string, as with the gram fields.
 */
export const normalisePiecesPerPack = (value) => {
  if (value === null || value === undefined || value === "" || typeof value === "boolean") return null;
  const number = asNumber(value);
  if (!Number.isInteger(number) || number < 2 || number > MAX_PIECES_PER_PACK) return null;
  return number;
};

/**
 * The per-weight map as stored: weights spelled the way article codes spell them ("125", not
 * "125.0"), only the weights that are packed per several. An empty map for nothing at all.
 */
export const normalisePiecesByWeight = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const result = {};
  Object.keys(value)
    .map((weight) => [normaliseWeight(weight), normalisePiecesPerPack(value[weight])])
    .filter(([weight, pieces]) => weight !== null && pieces !== null)
    .sort(([a], [b]) => compareWeights(a, b))
    .forEach(([weight, pieces]) => {
      result[weight] = pieces;
    });
  return result;
};

/** The pieces per package of one article of this product: null is one per package. */
export const piecesPerPackFor = (product, weight) => {
  if (!product) return null;
  const normalised = normaliseWeight(weight);
  if (normalised === null) return normalisePiecesPerPack(product.piecesPerPack);
  return normalisePiecesByWeight(product.piecesPerPackByWeight)[normalised] || null;
};

/**
 * What someone typed in one field: an empty field and 1 both mean "per stuk" and clear the value,
 * anything else that is not a whole number from 1 to the maximum is refused rather than quietly
 * cleared - a typo must not make the scanner count per piece again.
 */
export const parsePiecesPerPackInput = (value) => {
  if (value === null || value === undefined || (typeof value === "string" && !value.trim())) {
    return { ok: true, value: null, empty: true };
  }
  const number = asNumber(value);
  if (!Number.isInteger(number) || number < 1 || number > MAX_PIECES_PER_PACK) return { ok: false, value: null, empty: false };
  return { ok: true, value: number === 1 ? null : number, empty: false };
};

/** The same for the per-weight fields at once: the map to store (null for none), or which weights are wrong. */
export const parsePiecesByWeightInput = (values) => {
  if (values === null || values === undefined) return { ok: true, value: null, invalid: [] };
  if (typeof values !== "object" || Array.isArray(values)) return { ok: false, value: null, invalid: [] };
  const invalid = [];
  const valid = {};
  Object.keys(values).forEach((weight) => {
    const parsed = parsePiecesPerPackInput(values[weight]);
    if (!parsed.ok) invalid.push(weight);
    else if (parsed.value !== null) valid[weight] = parsed.value;
  });
  if (invalid.length > 0) return { ok: false, value: null, invalid };
  const map = normalisePiecesByWeight(valid);
  return { ok: true, value: Object.keys(map).length > 0 ? map : null, invalid: [] };
};

/** A quantity in what the picker takes: whole packages first, the rest loose. */
export const packsFor = (quantity, piecesPerPack) => {
  const size = normalisePiecesPerPack(piecesPerPack) || 1;
  const units = Number.isInteger(quantity) && quantity > 0 ? quantity : 0;
  if (size === 1) return { size, packs: 0, loose: units };
  return { size, packs: Math.floor(units / size), loose: units % size };
};

const positiveInteger = (value) => {
  const number = asNumber(value);
  return Number.isInteger(number) && number > 0 ? number : null;
};

/**
 * The webshop's besteleenheden of one article: the steps a customer can order it in. A variation
 * may have its own (the webshop's getEffectiveOrderUnit), otherwise the product's applies; no
 * besteleenheid is 1. Empty for a weight only a package asks for: nobody orders that one itself.
 */
export const orderUnitsForArticle = (product, weight) => {
  if (!product) return [1];
  const own = positiveInteger(product.orderUnit) || 1;
  const normalised = normaliseWeight(weight);
  if (normalised === null) return [own];
  const units = new Set(
    (Array.isArray(product.variations) ? product.variations : [])
      .filter((variation) => !!variation && normaliseWeight(variation.weight) === normalised)
      .map((variation) => positiveInteger(variation.orderUnit) || own),
  );
  return [...units].sort((a, b) => a - b);
};

export const PACK_ADVICE = {
  /** Nothing to say: per stuk, and the webshop sells per stuk too. */
  NONE: "none",
  /** Not filled in, while the webshop sells it in steps: probably packed that way. */
  SUGGEST: "suggest",
  /** Filled in, and every besteleenheid is whole packages. */
  FITS: "fits",
  /** Filled in, but a customer can order a quantity that is not whole packages. */
  LOOSE: "loose",
};

/**
 * What the product screen says about one article's number: whether the webshop's besteleenheid
 * suggests one, and whether the number fits what customers can order.
 *
 * The besteleenheid is a hint and never used as the value. It is a choice of the shop, not a fact
 * about the package: "Grill burger" is ordered per 20 and may well lie in bags of 10, and taking
 * 20 then would send the picker for one bag of ten.
 */
export const packAdvice = (orderUnits, piecesPerPack) => {
  const size = normalisePiecesPerPack(piecesPerPack);
  const units = orderUnits || [];
  const stepped = units.filter((unit) => unit > 1);
  if (!size) return stepped.length > 0 ? { kind: PACK_ADVICE.SUGGEST, orderUnits: stepped } : { kind: PACK_ADVICE.NONE };
  const misfits = units.filter((unit) => unit % size !== 0);
  return misfits.length > 0 ? { kind: PACK_ADVICE.LOOSE, orderUnits: misfits, size } : { kind: PACK_ADVICE.FITS, size };
};

/**
 * Per article of a product (`articles` from buildArticleCatalogue): its number and what the
 * besteleenheid says about it. Null for a package, whose components are picked, each in its own
 * packaging.
 */
export const articlePacks = (product, articles) => {
  if (!product || isPackageProduct(product)) return null;
  return (articles || []).map((article) => {
    const piecesPerPack = piecesPerPackFor(product, article.weight);
    return {
      articleCode: article.articleCode,
      weight: article.weight,
      sources: article.sources || [],
      piecesPerPack,
      advice: packAdvice(orderUnitsForArticle(product, article.weight), piecesPerPack),
    };
  });
};

/** Whether someone should look at these numbers: one is missing while sold in steps, or does not fit them. */
export const needsPackCheck = (packs) =>
  (packs || []).some((pack) => [PACK_ADVICE.SUGGEST, PACK_ADVICE.LOOSE].includes(pack.advice.kind));
